import {
	ChangeDetectionStrategy,
	Component,
	computed,
	EventEmitter,
	inject,
	Input,
	Output,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ChangePriorityComponent } from '../../manual/components/change-priority/change-priority.component';
import { ICON, ICON_STYLE } from '@core/util/icon';
import { openClose } from '@core/animations/animations';
import { MortgageDetailsOverview } from '../../mortgages.models';
import {
	MatterMortgageInstructionSource,
	MatterMortgageType,
	MatterMortgageWithAssociations,
} from '@models/mortgage';
import { Select, Store } from '@ngxs/store';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { Observable } from 'rxjs';
import {
	PersonMatterClientWithAssociations,
	BusinessMatterClientWithAssociations,
	MatterFileType,
	MatterFireInsurance,
} from '@models/matter';
import { MatterClients } from '@state/global/matter_clients';
import { MortgageTransactionStatus } from '@models/mortgages/mortgage-base';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { BaseComponent } from '@core/base.component';
import { Lender } from '@models/lender';
import { getMortgagePrimaryLender } from '@core/util/mortgage';
import { UrlService } from '@services/url/url.service';
import { formatListForDisplay } from '@core/util/string';

interface VM {
	mortgage: MatterMortgageWithAssociations;
	primaryLender: Lender;
}

const defaultVM: VM = {
	mortgage: null,
	primaryLender: null,
};

@Component({
	selector: 'app-mortgage-header',
	templateUrl: './mortgage-header.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	animations: [openClose()],
	standalone: false,
})
export class MortgageHeaderComponent extends BaseComponent<VM> {
	private store = inject(Store);

	@Input() details: MortgageDetailsOverview;
	@Input() set mortgage(mortgage: MatterMortgageWithAssociations) {
		this.VM = {
			mortgage,
			primaryLender: getMortgagePrimaryLender(mortgage),
		};
	}

	@Output() onViewAll: EventEmitter<void> = new EventEmitter<void>();
	@Output() goToInstructions: EventEmitter<void> = new EventEmitter<void>();

	@Select(SelectedMatter.closingDate) closingDate$: Observable<string>;
	@Select(MatterClients.get_mortgagorsWithAssociationMapped) matterClients$: (
		| PersonMatterClientWithAssociations
		| BusinessMatterClientWithAssociations
	)[];
	@Select(MatterClients.get_buyers) borrowers$: (
		| PersonMatterClientWithAssociations
		| BusinessMatterClientWithAssociations
	)[];
	@SelectSnapshot(SelectedMatter.matterFileType) fileType: MatterFileType;
	@SelectSnapshot(SelectedMatter.id) matterId: string;
	fireInsurance = this.store.selectSignal(SelectedMatter.fireInsurance);

	missingFireInsurancePropertiesString = computed(() => {
		const fireInsurance: MatterFireInsurance = this.fireInsurance();
		const missingProps = [];

		const hasEntry = Object.values(fireInsurance ?? {}).some(
			(value) => !!value,
		);

		if (hasEntry) {
			if (!fireInsurance?.fireInsuranceCompany) {
				missingProps.push('Fire Insurance Company');
			}
			if (!fireInsurance?.policyNumber) {
				missingProps.push('Policy Number');
			}
			if (!fireInsurance?.coverageAmount) {
				missingProps.push('Coverage Amount');
			}
			if (!fireInsurance?.activeDate) {
				missingProps.push('Active Date');
			}
		}

		return formatListForDisplay(missingProps);
	});

	showMissingProperties = computed(() => {
		return this.missingFireInsurancePropertiesString().length > 1;
	});

	public showSummary = false;
	public ICON = ICON;
	public ICON_STYLE = ICON_STYLE;
	public mortgageStatuses = MortgageTransactionStatus;
	public fileTypes = MatterFileType;
	public MatterMortgageInstructionSource = MatterMortgageInstructionSource;
	public MatterMortgageType = MatterMortgageType;
	public fireUrl: string;

	constructor(private matDialog: MatDialog) {
		super(defaultVM);
		this.fireUrl = UrlService.Matter_Property_FireInsurance(this.matterId);
	}

	editPriority(): void {
		this.matDialog.open(ChangePriorityComponent);
	}

	toggleShowSummary(): void {
		this.showSummary = !this.showSummary;
	}

	viewAll() {
		this.showSummary = false;
		this.onViewAll.emit();
	}

	handleGoToInstructions($event) {
		$event.preventDefault();
		$event.stopPropagation();
		this.goToInstructions.emit();
	}

	get headerTitle(): string {
		if (this.fileType === MatterFileType.SALE) {
			return 'Mortgage Details';
		}
		return `Mortgage Details (${this.VM.mortgage?.transactionType?.toUpperCase()})`;
	}
}
